import {
  authorizeUrl,
  challengeFor,
  exchangeCode,
  randomUrlSafe,
  readCallback,
  rememberPending,
  takePending,
} from './pkce.js'
import type { AuthConfig, TokenSet } from './pkce.js'

/**
 * Holds the token in memory only.
 *
 * Not localStorage or sessionStorage: a token in storage survives the tab and is
 * readable by any injected script. In memory the blast radius of an XSS is
 * bounded by the page's lifetime, and a reload simply re-runs the silent
 * redirect. The token is short-lived anyway — 15 minutes.
 */
let current: TokenSet | undefined

export const beginSignIn = async (config: AuthConfig, storage: Storage): Promise<string> => {
  const verifier = randomUrlSafe()
  const state = randomUrlSafe(16)
  rememberPending(storage, { verifier, state })
  return authorizeUrl(config, await challengeFor(verifier), state)
}

export const completeSignIn = async (
  config: AuthConfig,
  storage: Storage,
  search: string,
): Promise<TokenSet> => {
  const pending = takePending(storage)
  const result = readCallback(search, pending?.state)

  if (result.kind === 'error') throw new Error(result.message)
  if (result.kind === 'none') throw new Error('No authorization code in the callback')
  if (pending === undefined) throw new Error('No sign-in was in progress in this tab')

  current = await exchangeCode(config, result.code, pending.verifier)
  return current
}

export const currentToken = (now: () => number = Date.now): string | undefined =>
  current !== undefined && current.expiresAt > now() ? current.idToken : undefined

export const signOut = (): void => {
  current = undefined
}
